import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import { useNavigate } from 'react-router-dom';
import { isReauthRequired } from '@/lib/reauth';
import { extractReauthError } from '@/lib/reauth';
import { useReauth } from '@/contexts/ReauthContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useSelection } from '@/contexts/SelectionContext';
import { GOOGLE_AUTH_STATUS_QUERY_KEY } from '@/hooks/useGoogleAuthStatus';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { AccountCardSkeleton } from '@/components/skeletons/AccountCardSkeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Trash2, AlertTriangle, Plus, RefreshCw, CheckCircle2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { de, enUS } from 'date-fns/locale';
import GoogleOAuthButton from '@/components/GoogleOAuthButton';
import SyncProgressDialog from '@/components/SyncProgressDialog';
import McLimitNotice from '@/components/McLimitNotice';
import { parseMcLimitBlocked } from '@/lib/mcLimitMarker';
import { isOwnerMustTransferError, firstEmbedded, collectEmbedded } from '@/lib/mcMembership';
import GoogleAccountWizard from '@/components/wizard/GoogleAccountWizard';
import { PageHeader } from '@/components/PageHeader';
import { GoogleAdsAccountRow } from '@/components/settings/GoogleAdsAccountRow';

interface GoogleAccount {
  id: string;
  google_account_id: string;
  email: string;
  display_name: string | null;
  created_at: string;
  updated_at: string;
  token_expires_at: string | null;
  /**
   * Google-Account-Auth-Lifecycle (migrations 20260726035343 + 20260726180000).
   * 'reauth_required' means Google itself rejected the credentials — usually because
   * the user hit "Remove access" in their Google account. 'scope_missing' means consent
   * completed without the Merchant API scope, so the tokens are alive but blind.
   * token_expires_at cannot show either: it is a local clock and stays in the future
   * while the token is already useless.
   */
  auth_status: string | null;
  /**
   * Letzte Sync-Meldung von fetch-account-data. Traegt sie den Marker
   * `mc_limit_blocked:<n>` (MC-Anzahl-Gate des Preisumbaus), zeigt die Karte
   * den lokalisierten Add-on-Hinweis, bis ein Sync ohne Blockierungen ihn
   * ueberschreibt.
   */
  sync_message: string | null;
}

const GoogleAccounts: React.FC = () => {
  const { t, i18n } = useTranslation('settings');
  const { user } = useAuth();
  const navigate = useNavigate();
  const { triggerReauth } = useReauth();
  const queryClient = useQueryClient();
  const { selectedGoogleAccountId, setSelectedGoogleAccount, clearSelection } = useSelection();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [accountToDelete, setAccountToDelete] = useState<GoogleAccount | null>(null);
  const [refreshingAccount, setRefreshingAccount] = useState<string | null>(null);
  const [syncProgressOpen, setSyncProgressOpen] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);

  const { data: accounts, isLoading } = useQuery({
    queryKey: ['google-accounts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('google_accounts')
        .select('id, google_account_id, email, display_name, created_at, updated_at, token_expires_at, auth_status, sync_message')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as GoogleAccount[];
    },
  });

  /**
   * Die Merchant Center aller Konten, fuer die Ads-Zeile je Karte.
   *
   * Der `SelectionContext` fuehrt nur das EINE aktive Merchant Center; die
   * Karten brauchen aber je Google-Konto seine eigenen (Anzeigename der
   * Zuordnung und das Ziel, an das ein Ads-Konto gehaengt wird). Spaltenweise
   * benannt statt `select('*')`, wie ueberall in diesem Baum.
   *
   * Mitgliedschafts-Umstellung (Task 10): "MCs eines Google-Kontos" heisst
   * seit der Spec "MCs, fuer die dieses Konto der Grant einer Mitgliedschaft
   * ist" -- die Basistabelle ist deshalb merchant_center_members statt
   * merchant_centers direkt. `!inner` auf merchant_centers filtert
   * Mitgliedschaftszeilen ohne sichtbare MC weg; die Einbettung bleibt trotzdem
   * defensiv per firstEmbedded entpackt (ueber `collectEmbedded`, siehe
   * `lib/mcMembership.ts`, Fix-Runde 1, Important 1 -- der Flatten/Dedupe-
   * Kern dieser Schleife stand vorher woertlich kopiert in fuenf Dateien).
   *
   * Fix-Runde 1 (Important 2): `.eq('user_id', user.id)` filtert auf die
   * EIGENEN Mitgliedschaftszeilen -- ohne den Filter liefert die Abfrage seit
   * der Mitgliedschafts-Umstellung Zeilen ALLER Nutzer, die an einer geteilten
   * MC haengen (deren `google_account_id`-UUIDs gehoeren dann fremden
   * Konten). Die Karten dieser Seite sind je eine je EIGENES Google-Konto
   * (`accounts`-Query oben, ebenfalls implizit auf den Nutzer gescoped ueber
   * RLS auf `google_accounts`); ohne den Filter haette `GoogleAdsAccountRow`
   * potenziell die MC eines fremden Nutzers als Ziel angeboten.
   */
  const { data: merchantCenters, error: merchantCentersError } = useQuery({
    queryKey: ['merchant-centers', 'google-accounts-page'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('merchant_center_members')
        .select('google_account_id, merchant_centers!inner(id, name)')
        .eq('user_id', user.id)
        .order('joined_at', { ascending: true });

      if (error) throw error;

      const rows = data ?? [];

      return collectEmbedded(
        rows,
        (row) => row.merchant_centers,
        (row, mc) => `${row.google_account_id}:${mc.id}`,
      ).map(({ row, embed }) => ({ id: embed.id, name: embed.name, google_account_id: row.google_account_id }));
    },
  });

  /**
   * Ein Ausfall dieser Abfrage ist in beide Richtungen still: JEDE Karte
   * bekaeme eine leere Liste, die Ads-Zeile boete dann nirgends mehr eine
   * Zuordnung an (sie verlangt genau ein Merchant Center) und zeigte statt des
   * MC-Namens die maskierte Kundennummer. Das sieht nach einem Zustand aus,
   * ist aber ein Fehler, also gehoert es gemeldet.
   */
  React.useEffect(() => {
    if (!merchantCentersError) return;
    Sentry.captureException(merchantCentersError, {
      tags: { feature: 'ads-chapter', stage: 'settings-merchant-centers' },
    });
  }, [merchantCentersError]);

  const deleteMutation = useMutation({
    mutationFn: async (accountId: string) => {
      // Austritt-Schutz PRE-CHECK (Fix-Runde 1, Important 4): der Aufruf von
      // unregister-merchant-centers direkt darunter ist bei Google IRREVERSIBEL,
      // und der DB-Trigger guard_owner_grant_delete sitzt erst auf dem
      // `DELETE FROM google_accounts` DANACH -- ohne diesen Vorablauf kaeme
      // die OWNER_MUST_TRANSFER-Ablehnung erst an, nachdem der Google-seitige
      // Schritt schon gelaufen ist, zu spaet, um ihn zu verhindern. Dieselbe
      // Bedingung wie der Trigger, nur vorher und clientseitig; RLS laesst den
      // Owner alle Mitgliedszeilen seiner eigenen MCs lesen.
      const { data: ownerGrantRows, error: ownerCheckError } = await supabase
        .from('merchant_center_members')
        .select('merchant_centers(name, merchant_center_id, merchant_center_members(user_id))')
        .eq('google_account_id', accountId)
        .eq('role', 'owner');

      if (ownerCheckError) {
        // Fail-closed: eine gescheiterte Pruefung gilt NIE als "sauber" -- sie
        // faellt in den generischen Fehlerpfad (kein OWNER_MUST_TRANSFER im
        // Text, siehe onError unten), unregister wird gar nicht erst gerufen.
        throw new Error(`Merchant center ownership check failed: ${ownerCheckError.message}`);
      }

      const ownerRows = ownerGrantRows ?? [];
      const blockingNames: string[] = [];
      for (const row of ownerRows) {
        const mc = firstEmbedded(row.merchant_centers);
        if (!mc) continue;
        const members = Array.isArray(mc.merchant_center_members)
          ? mc.merchant_center_members
          : mc.merchant_center_members
            ? [mc.merchant_center_members]
            : [];
        const hasOtherMember = members.some((m) => m.user_id !== user?.id);
        if (hasOtherMember) {
          blockingNames.push(mc.name || mc.merchant_center_id);
        }
      }
      if (blockingNames.length > 0) {
        // isOwnerMustTransferError prueft `.message` per `.includes(...)`, ein
        // angehaengter Klartext der MC-Namen bleibt darum erkennbar und hilft
        // beim Nachsehen in Sentry/Konsole, ohne den Toast-Text zu aendern
        // (der kommt fest aus i18n, siehe onError unten).
        throw new Error(`OWNER_MUST_TRANSFER: ${blockingNames.join(', ')}`);
      }

      // First, unregister merchant centers from Google Cloud Project
      const unregisterRes = await supabase.functions.invoke('unregister-merchant-centers', {
        body: { googleAccountId: accountId },
      });

      if (unregisterRes.error) {
        console.error('Unregistration error:', unregisterRes.error);
        throw new Error('Failed to unregister merchant centers');
      }

      // Then delete the Google account (cascades to related data)
      const { error } = await supabase
        .from('google_accounts')
        .delete()
        .eq('id', accountId);

      if (error) throw error;

      return unregisterRes.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['google-accounts'] });
      queryClient.invalidateQueries({ queryKey: ['merchant-centers'] });
      queryClient.invalidateQueries({ queryKey: ['feeds'] });
      queryClient.invalidateQueries({ queryKey: ['user-selections'] });

      // Clear selection if the deleted account was the active one
      if (accountToDelete?.id === selectedGoogleAccountId) {
        clearSelection();
      }

      const merchantCenters = data?.merchantCenters?.unregistered || 0;
      const supplementalFeeds = data?.supplementalFeeds?.deleted || 0;
      const failedMerchants = data?.merchantCenters?.failed || 0;
      const failedFeeds = data?.supplementalFeeds?.failed || 0;

      let description = t('googleAccounts.deleteSuccessDesc');
      if (merchantCenters > 0 || supplementalFeeds > 0) {
        description += ` ${t('googleAccounts.deleteSuccessDetails', { merchantCenters, supplementalFeeds })}`;
      }

      toast.success(t('googleAccounts.deleteSuccess'), {
        description,
      });
      setDeleteDialogOpen(false);
      setAccountToDelete(null);
    },
    onError: (error) => {
      // Austritt-Schutz (Spec 6.2): der DB-Trigger guard_owner_grant_delete
      // wirft OWNER_MUST_TRANSFER, wenn dieses Google-Konto der Owner-Grant
      // einer Merchant Center mit weiteren Mitgliedern ist. Eigener Text statt
      // des generischen "Konto konnte nicht geloescht werden" -- der Nutzer
      // muss wissen, DASS und WARUM es hier eine Uebergabe braucht.
      if (isOwnerMustTransferError(error)) {
        toast.error(t('googleAccounts.ownerMustTransfer'));
        return;
      }
      toast.error(t('status.error', { ns: 'common' }), {
        description: t('googleAccounts.deleteError', { error: error.message }),
      });
    },
  });

  const handleDeleteClick = (account: GoogleAccount) => {
    setAccountToDelete(account);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (accountToDelete) {
      deleteMutation.mutate(accountToDelete.id);
    }
  };

  const handleRefreshAccountData = async (accountId: string) => {
    setRefreshingAccount(accountId);

    // Reset sync_status in DB BEFORE opening dialog to prevent
    // the dialog from immediately seeing stale 'complete' status
    await supabase
      .from('google_accounts')
      .update({ sync_status: 'pending', sync_message: null, sync_progress: 0 })
      .eq('id', accountId);

    setSyncProgressOpen(true);

    try {
      const res = await supabase.functions.invoke('fetch-account-data', {
        body: {
          googleAccountId: accountId,
          userId: user?.id,
        },
      });

      if (isReauthRequired(res)) {
        const reauthError = await extractReauthError(res);
        setSyncProgressOpen(false);
        setRefreshingAccount(null);
        triggerReauth(reauthError || { googleAccountId: accountId });
        return;
      }

      if (res.error) {
        setSyncProgressOpen(false);
        setRefreshingAccount(null);
        throw res.error;
      }

      if (res.data?.success === false) {
        setSyncProgressOpen(false);
        setRefreshingAccount(null);
        if (res.data.code === 'MISSING_API_DEVELOPER') {
          toast.error(t('googleAccounts.apiDeveloperRequired'), {
            description: res.data.hint || res.data.message,
          });
        } else {
          toast.error(t('googleAccounts.refreshFailed'), {
            description: res.data.message || t('googleAccounts.refreshFailedDesc'),
          });
        }
        return;
      }

      // Success: Dialog closes via onComplete callback when sync_status='complete' is polled
      // Show toast as additional confirmation
      toast.success(t('googleAccounts.dataRefreshed'), {
        description: res.data?.summary || t('googleAccounts.dataRefreshedDesc'),
      });
    } catch (error) {
      console.error('Error refreshing account data:', error);
      Sentry.captureException(error, { tags: { feature: 'google-accounts', action: 'refresh' } });
      setSyncProgressOpen(false);
      setRefreshingAccount(null);
      toast.error(t('googleAccounts.refreshFailed'), {
        description: error instanceof Error ? error.message : t('googleAccounts.refreshFailedDesc'),
      });
    }
  };

  const handleReconnectSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['google-accounts'] });
    // The banner reads a DIFFERENT query (useGoogleAuthStatus). Refreshing only this
    // page's list left the red top banner standing after a successful reconnect
    // whenever the realtime socket had dropped — staleTime is 30s and
    // refetchOnWindowFocus is off, so nothing else would have refetched it this session.
    queryClient.invalidateQueries({ queryKey: [GOOGLE_AUTH_STATUS_QUERY_KEY] });
    toast.success(t('googleAccounts.reconnected'), {
      description: t('googleAccounts.reconnectedDesc'),
    });

    // Check if there's a redirect path stored
    const redirectPath = sessionStorage.getItem('redirectAfterReauth');
    if (redirectPath) {
      sessionStorage.removeItem('redirectAfterReauth');
      setTimeout(() => {
        window.location.href = redirectPath;
      }, 1000);
    }
  };

  const handleSelectAccount = async (accountId: string) => {
    try {
      await setSelectedGoogleAccount(accountId);
      toast.success(t('googleAccounts.accountSelected'), {
        description: t('googleAccounts.accountSelectedDesc'),
      });
    } catch (error) {
      console.error('Error selecting account:', error);
      Sentry.captureException(error, { tags: { feature: 'google-accounts', action: 'select' } });
      toast.error(t('googleAccounts.selectionFailed'), {
        description: t('googleAccounts.selectionFailedDesc'),
      });
    }
  };

  const getInitials = (email: string) => {
    return email.charAt(0).toUpperCase();
  };

  /**
   * Jede Avatar-Farbe bringt ihren eigenen Vordergrund mit, statt dass die
   * Aufrufstelle pauschal `text-white` setzt: im Dark Mode ist `--primary`
   * eine HELLE Flaeche, weisse Schrift darauf reisst auf 1.49 bis 2.37:1
   * (Regel 9). Jedes Paar unten ist Flaeche plus ihr eigenes
   * `*-foreground`-Token, das den Unterschied zwischen den Themes kennt.
   *
   * Warum Token statt Rohpalette: eine Tailwind-Rohfarbe (`bg-blue-700`,
   * die violette 700er-Stufe als bg-Klasse, ...) gehoert keiner Farbwelt
   * an und ueberlebt einen Rebrand deshalb still -- genau das ist hier
   * passiert, vier der fuenf Klassen unten trugen bis zum Petrol-Umbau
   * noch eine Rohpalettenstufe (zwei davon, blue-700 und die 700er-Stufe
   * von violet, aus der Indigo-Vorgaengerwelt; orange-700 und pink-700
   * waren nie an ein Token gebunden).
   *
   * Gemessen wurde gegen 4.5:1, nicht gegen 3:1 -- die Initialen rendern mit
   * `text-lg` (18 px) bei Gewicht 400, und "large text" beginnt nach WCAG
   * erst bei 24 px normal bzw. 18.66 px fett. 18 px normal ist also normaler
   * Text.
   *
   * Fuenf Paare, alle >= 4.5:1 in BEIDEN Themes (Petrol-und-Sand-Tokens,
   * `src/tokens.generated.css` + `src/index.css`):
   *   bg-primary text-primary-foreground           7.70 hell / 8.06 dunkel
   *   bg-success text-success-foreground            5.39 hell / 6.84 dunkel
   *   bg-primary-ink text-primary-foreground        10.41 hell / 10.08 dunkel
   *   bg-info text-info-foreground                  6.59 hell / 6.33 dunkel
   *   bg-warning text-warning-foreground             4.71 hell / 6.98 dunkel
   *
   * Die fuenfte Stelle war im Kandidatensatz eigentlich
   * `bg-[hsl(var(--dark))] text-primary-foreground` vorgesehen (S2-Notiz),
   * aber `--dark` steht bislang in keinem der beiden App-CSS-Bloecke -- der
   * Token-Generator (`APP_VAR_MAP` in `scripts/build-design-tokens.mjs`)
   * emittiert ihn fuer die App noch nicht. Die Variable existiert also gar
   * nicht (die Flaeche rendert transparent statt der beabsichtigten Farbe),
   * und selbst wenn sie es taete, waere der Kandidat am Kontrast
   * gescheitert: `--dark` ist laut `design-tokens/tokens.json` in beiden
   * Themes `#0B2A2C`, im Dark Mode praktisch identisch mit
   * `--primary-foreground` (184 60% 11%) -- 1.01:1, weit unter AA.
   * `bg-warning text-warning-foreground` tritt an ihre Stelle.
   *
   * Historie (B4c, Vorgaenger-Farbwelt, Hex-Literale weil theme-unabhaengig):
   * alle vier damaligen Kandidaten fielen durch -- weiss auf bg-info 3.64:1
   * hell / 2.52:1 dunkel, die violette Flaeche 4.32 / 2.77, bg-orange-500
   * 2.80, bg-pink-500 3.53 (die letzten beiden theme-unabhaengig, daher nur
   * ein Wert). Die damaligen Ersatzstufen blue-700 (6.70), die 700er-Stufe
   * von violet (7.10), orange-700 (5.18) und pink-700 (6.04) bestanden AA,
   * fielen aber trotzdem: eine Rohfarbe traegt kein Token und haette den
   * Petrol-Umbau still ueberlebt. Messwerte + Regressionsschutz:
   * `avatarContrast.test.ts`.
   */
  const getAvatarColor = (email: string) => {
    const colors = [
      'bg-primary text-primary-foreground',
      'bg-success text-success-foreground',
      'bg-primary-ink text-primary-foreground',
      'bg-info text-info-foreground',
      'bg-warning text-warning-foreground',
    ];
    const index = email.charCodeAt(0) % colors.length;
    return colors[index];
  };

  /**
   * The connection badge hangs on auth_status ONLY (K06). token_expires_at is the
   * expiry of the hourly ACCESS token; getValidAccessToken renews it on demand via
   * the refresh token, so an expired access token says nothing about the
   * connection. Deriving "disconnected" from it painted every healthy card red
   * (live 06.10.2026: 24 of 24 active accounts).
   *
   * auth_status carries Google's real verdict, written by the edge functions on a
   * genuine 401: 'reauth_required' (access revoked) and 'scope_missing' (the
   * Merchant API checkbox stayed unticked during consent, Finding R2-3).
   *
   * Statuses are listed explicitly rather than tested with !== 'active'. If PostgREST
   * ever stopped delivering the column, the negated form would flip EVERY account to
   * "disconnected"; this form keeps unknown or missing values connected.
   */
  const missingScope = (account: GoogleAccount) => account.auth_status === 'scope_missing';

  const needsReconnect = (account: GoogleAccount) =>
    account.auth_status === 'reauth_required' || missingScope(account);

  const hasAccounts = accounts && accounts.length > 0;

  return (
    <>
      <div className="space-y-6">
        <PageHeader
          title={t('googleAccounts.title')}
          subtitle={t('googleAccounts.subtitle')}
          actions={
            !isLoading ? (
              <Button
                className="flex items-center space-x-2 w-full sm:w-auto"
                onClick={() => setWizardOpen(true)}
              >
                <Plus className="h-4 w-4" />
                <span>{t('googleAccounts.addAccount')}</span>
              </Button>
            ) : undefined
          }
          as="h2"
        />

        {/* Bericht 3.7: kompakte OAuth-Erklaerung an der zweiten Connect-Stelle.
            Statische Hinweiszeile statt Modal, damit die Popup-Oeffnung der
            Connect-/Reconnect-Buttons synchron im Klick-Handler bleibt. */}
        <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
          <ShieldCheck className="h-3.5 w-3.5 flex-shrink-0 mt-0.5 text-primary" />
          <span>{t('googleAccounts.oauthScopeHint')}</span>
        </p>

        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <AccountCardSkeleton key={i} />
            ))}
          </div>
        ) : !hasAccounts ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <div className="text-center space-y-4">
                <div className="mx-auto w-12 h-12 bg-muted rounded-full flex items-center justify-center">
                  <Plus className="h-6 w-6 text-muted-foreground" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold">{t('googleAccounts.getStarted')}</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    {t('googleAccounts.getStartedDesc')}
                  </p>
                </div>
                <Button onClick={() => setWizardOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  {t('googleAccounts.connectAccount')}
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {accounts.map((account) => {
              const reconnectNeeded = needsReconnect(account);
              const scopeMissing = missingScope(account);
              const isSelected = selectedGoogleAccountId === account.id;
              const isAuthAccount = user?.email?.toLowerCase() === account.email?.toLowerCase();
              // MC-Anzahl-Gate: der letzte Sync hat neue Merchant Center ueber
              // der Tarif-Grenze nicht verbunden (Marker in sync_message).
              const mcBlockedCount = parseMcLimitBlocked(account.sync_message);

              return (
                <Card 
                  key={account.id} 
                  className={`hover:shadow-md transition-all ${
                    isSelected ? 'ring-2 ring-primary' : ''
                  }`}
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center space-x-3 flex-1 min-w-0">
                        <Avatar className="h-12 w-12">
                          <AvatarFallback className={`${getAvatarColor(account.email)} text-lg`}>
                            {getInitials(account.email)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <CardTitle className="text-base truncate">
                              {account.display_name || account.email}
                            </CardTitle>
                            {isSelected && (
                              <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0" />
                            )}
                          </div>
                          <CardDescription className="text-xs truncate">
                            {account.email}
                          </CardDescription>
                        </div>
                      </div>
                      {isAuthAccount ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-muted-foreground cursor-not-allowed"
                                disabled
                                aria-label={t('googleAccounts.deleteAccount')}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{t('googleAccounts.cannotDeleteAuthAccount')}</p>
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive-ink hover:bg-destructive/10"
                          onClick={() => handleDeleteClick(account)}
                          aria-label={t('googleAccounts.deleteAccount')}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">{t('googleAccounts.id')}</span>
                      <code className="text-xs bg-muted px-1 py-0.5 rounded">
                        {account.google_account_id?.slice(0, 12) || 'N/A'}...
                      </code>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">{t('googleAccounts.lastUpdated')}</span>
                      <span className={`flex items-center gap-1 ${
                        account.updated_at && (Date.now() - new Date(account.updated_at).getTime()) > 24 * 60 * 60 * 1000
                          ? 'text-warning-ink'
                          : ''
                      }`}>
                        {account.updated_at && (Date.now() - new Date(account.updated_at).getTime()) > 24 * 60 * 60 * 1000 && (
                          <AlertTriangle className="h-3 w-3" />
                        )}
                        {account.updated_at
                          ? formatDistanceToNow(new Date(account.updated_at), { addSuffix: true, locale: i18n.language === 'de' ? de : enUS })
                          : t('status.unknown', { ns: 'common' })}
                      </span>
                    </div>
                    
                    {isSelected && (
                      <Badge variant="default" className="w-full justify-center">
                        {t('googleAccounts.activeAccount')}
                      </Badge>
                    )}

                    {mcBlockedCount !== null && (
                      <McLimitNotice blockedCount={mcBlockedCount} />
                    )}

                    <GoogleAdsAccountRow
                      googleAccountId={account.id}
                      merchantCenters={(merchantCenters ?? [])
                        .filter((mc) => mc.google_account_id === account.id)
                        .map((mc) => ({ id: mc.id, name: mc.name }))}
                    />
                    
                    <div className="flex gap-2 flex-col">
                      <div className="flex gap-2">
                        <Badge
                          variant={reconnectNeeded ? "destructive" : "outline"}
                          className="flex-1 justify-center"
                        >
                          {scopeMissing
                            ? t('googleAccounts.permissionMissing')
                            : reconnectNeeded
                              ? t('status.disconnected', { ns: 'common' })
                              : t('status.connected', { ns: 'common' })}
                        </Badge>
                      </div>

                      <div className="flex gap-2">
                        {!isSelected && !reconnectNeeded && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleSelectAccount(account.id)}
                            className="flex-1"
                          >
                            {t('googleAccounts.setAsActive')}
                          </Button>
                        )}
                        
                        {reconnectNeeded ? (
                          <GoogleOAuthButton onSuccess={handleReconnectSuccess}>
                            <Button
                              variant="default"
                              size="sm"
                              className="flex-1"
                            >
                              {scopeMissing
                                ? t('googleAccounts.grantPermission')
                                : t('googleAccounts.reconnect')}
                            </Button>
                          </GoogleOAuthButton>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleRefreshAccountData(account.id)}
                            disabled={refreshingAccount === account.id}
                            className="flex-1"
                          >
                            {refreshingAccount === account.id ? (
                              <>
                                <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                                {t('actions.refreshing', { ns: 'common' })}
                              </>
                            ) : (
                              <>
                                <RefreshCw className="h-3 w-3 mr-1" />
                                {t('actions.refreshData', { ns: 'common' })}
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      <SyncProgressDialog 
        open={syncProgressOpen}
        googleAccountId={refreshingAccount}
        onComplete={() => {
          setSyncProgressOpen(false);
          setRefreshingAccount(null);
          queryClient.invalidateQueries({ queryKey: ['google-accounts'] });
          queryClient.invalidateQueries({ queryKey: ['merchant-centers'] });
          queryClient.invalidateQueries({ queryKey: ['feeds'] });
        }}
      />

      <GoogleAccountWizard
        mode="connect"
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onSuccess={async (googleAccountId) => {
          if (googleAccountId) {
            await setSelectedGoogleAccount(googleAccountId);
          }
          queryClient.invalidateQueries({ queryKey: ['google-accounts'] });
          queryClient.invalidateQueries({ queryKey: ['merchant-centers'] });
          queryClient.invalidateQueries({ queryKey: ['feeds'] });
        }}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <div className="flex items-center space-x-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              <AlertDialogTitle>{t('googleAccounts.deleteTitle')}</AlertDialogTitle>
            </div>
            <AlertDialogDescription className="space-y-2">
              <p>
                {t('googleAccounts.deleteDesc', { email: accountToDelete?.email })}
              </p>
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li>{t('googleAccounts.deleteItemMerchantUnreg')}</li>
                <li>{t('googleAccounts.deleteItemSupplementalFeeds')}</li>
                <li>{t('googleAccounts.deleteItemMerchantCenters')}</li>
                <li>{t('googleAccounts.deleteItemDataFeeds')}</li>
                <li>{t('googleAccounts.deleteItemProducts')}</li>
                <li>{t('googleAccounts.deleteItemSelections')}</li>
              </ul>
              <p className="text-xs text-muted-foreground mt-2">
                {t('googleAccounts.deleteNote')}
              </p>
              <p className="font-semibold text-destructive">
                {t('googleAccounts.deleteWarning')}
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('actions.cancel', { ns: 'common' })}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? t('actions.deleting', { ns: 'common' }) : t('googleAccounts.deleteAccount')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default GoogleAccounts;
